import * as React from "react";
import { Paper, Typography, Chip, IconButton, Box, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ITfidTags } from "../services/registry/TfidTagsService";

export interface TfidTooltipProps {
  tfid: string;
  anchorPosition: { x: number; y: number };
  tagsInfo: ITfidTags | undefined;
  onEdit: () => void;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const TfidTooltip = ({ tfid, anchorPosition, tagsInfo, onEdit, onMouseEnter, onMouseLeave }: TfidTooltipProps) => {
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  React.useEffect(() => {
    const padding = 8;
    let top = anchorPosition.y + 16;
    let left = anchorPosition.x + 8;

    if (tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      if (left + rect.width > window.innerWidth - padding) {
        left = window.innerWidth - rect.width - padding;
      }
      if (top + rect.height > window.innerHeight - padding) {
        top = anchorPosition.y - rect.height - 8;
      }
    }

    setPosition({ top, left });
  }, [anchorPosition]);

  const handleCopy = () => {
    navigator.clipboard.writeText(tfid);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit();
  };

  return (
    <Paper
      ref={tooltipRef}
      elevation={8}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex: 9999,
        padding: "8px 12px",
        maxWidth: 360,
        minWidth: 200,
        pointerEvents: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontWeight: "bold",
            fontSize: "0.85rem",
            userSelect: "all",
          }}
        >
          {tfid}
        </Typography>
        <Box>
          <IconButton size="small" onClick={handleCopy} title="Copy TFID">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleEdit} title="Edit Tags">
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {tagsInfo?.label && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {tagsInfo.label}
        </Typography>
      )}

      {tagsInfo?.env && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          env: {tagsInfo.env}
        </Typography>
      )}

      {tagsInfo && tagsInfo.tags.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {tagsInfo.tags.map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      {!tagsInfo && (
        <Button size="small" variant="text" onClick={handleEdit} sx={{ mt: 0.5, textTransform: "none" }}>
          + Add tags
        </Button>
      )}
    </Paper>
  );
};
